import React from "react";

/**
 * Engineering-focused project card with professional styling.
 * Presents: problem, approach, stack, security, status.
 */
export default function ProjectCard({ project, expanded = false }) {
  const {
    title,
    tagline,
    problem,
    approach,
    stack,
    architecture,
    security,
    status,
    statusDetail,
    links,
  } = project;

  return (
    <article className="project-card">
      {/* Header */}
      <header className="project-header">
        <div className="project-header-content">
          <h3 className="project-title">{title}</h3>
          <p className="project-tagline">{tagline}</p>
        </div>
        <div className="project-status-wrapper">
          <span className={`project-status ${status === "Production" ? "status-production" : "status-development"}`}>
            {status}
          </span>
          {statusDetail && (
            <span className="project-status-detail">{statusDetail}</span>
          )}
        </div>
      </header>

      {/* Content */}
      <div className="project-content">
        {/* Problem */}
        <div className="project-section">
          <h4 className="project-section-title">Problem</h4>
          <p className="project-section-text">{problem}</p>
        </div>

        {/* Approach */}
        <div className="project-section">
          <h4 className="project-section-title">Approach</h4>
          <p className="project-section-text">{approach}</p>
        </div>

        {/* Stack */}
        <div className="project-section">
          <h4 className="project-section-title">Stack</h4>
          <div className="project-stack">
            {stack.map((tech) => (
              <span key={tech} className="stack-tag">
                {tech}
              </span>
            ))}
          </div>
        </div>

        {/* Architecture - shown if expanded */}
        {expanded && architecture && (
          <div className="project-section">
            <h4 className="project-section-title">Architecture</h4>
            <p className="project-section-text">{architecture}</p>
          </div>
        )}

        {/* Security - shown if expanded */}
        {expanded && security && security.length > 0 && (
          <div className="project-section">
            <h4 className="project-section-title">Security</h4>
            <ul className="project-security-list">
              {security.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Links */}
      {links && Object.keys(links).length > 0 && (
        <footer className="project-footer">
          {links.live && (
            <a
              href={links.live}
              target="_blank"
              rel="noopener noreferrer"
              className="project-link project-link-primary"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                <polyline points="15 3 21 3 21 9"/>
                <line x1="10" y1="14" x2="21" y2="3"/>
              </svg>
              Live Demo
            </a>
          )}
          {links.docs && (
            <a href={links.docs} className="project-link">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10 9 9 9 8 9"/>
              </svg>
              Documentation
            </a>
          )}
          {links.repo && (
            <a
              href={links.repo}
              target="_blank"
              rel="noopener noreferrer"
              className="project-link"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 19c-5 1.5-5-2.5-7-3m14 6v-3.87a3.37 3.37 0 0 0-.94-2.61c3.14-.35 6.44-1.54 6.44-7A5.44 5.44 0 0 0 20 4.77 5.07 5.07 0 0 0 19.91 1S18.73.65 16 2.48a13.38 13.38 0 0 0-7 0C6.27.65 5.09 1 5.09 1A5.07 5.07 0 0 0 5 4.77a5.44 5.44 0 0 0-1.5 3.78c0 5.42 3.3 6.61 6.44 7A3.37 3.37 0 0 0 9 18.13V22"/>
              </svg>
              Repository
            </a>
          )}
        </footer>
      )}
    </article>
  );
}
